/** Combat Lab calibration and inert targets; stage assembly lives in the facade. */
import { mobCombatPoint, mobSpriteCenter, playerCombatPoint } from '../../../shared/logic/CombatAnchors';

/** Top surface shared by the stage and every measured target. */
export const LAB_FLOOR_TOP_Y = 700;
/** Player hitbox is 40x40; dummy hitbox is 60x50. */
export const LAB_PLAYER_H = 40;
export const LAB_PLAYER_W = 40;
export const LAB_DUMMY_W = 60;
export const LAB_DUMMY_H = 50;
export const LAB_DUMMY_TYPE = 'training_dummy';
const COLOR_PROFILE = '#ff8c42';
const COLOR_CAP = '#ff4d4d';

// ============================================================================
// GEOMETRY HELPERS — the single place distance↔x is converted.
// ============================================================================

/**
 * The x-offset at which a target sitting `dy` off the facing axis is EXACTLY
 * `distancePx` away from the origin. Returns null when `distancePx` is shorter
 * than the off-axis offset itself (no such x exists).
 */
export function offsetForDistance(distancePx: number, dy: number): number | null {
  const squared = distancePx * distancePx - dy * dy;
  return squared > 0 ? Math.sqrt(squared) : null;
}

/** Bearing off the facing axis, in degrees — the angle `isInCone` compares to arcDeg/2. */
export function bearingDeg(dx: number, dy: number): number {
  return (Math.atan2(Math.abs(dy), Math.abs(dx)) * 180) / Math.PI;
}

/**
 * The y-offset from a station anchor to the COMBAT POINT of a dummy whose
 * hitbox top sits at `rowTopY` — i.e. to its sprite centre, which is what the
 * client cone hit-tests. Not to its authored corner: that was the v1.0 bug.
 */
function combatRowDy(rowTopY: number, anchorY: number): number {
  return mobSpriteCenter({ x: 0, y: rowTopY, width: LAB_DUMMY_W, height: LAB_DUMMY_H }).y - anchorY;
}

// ============================================================================
// STATION ANCHORS — the fixed player positions everything is measured from.
// ============================================================================

/**
 * Ruler station. This is `player.position` — the TOP-LEFT of the hitbox — for a
 * player standing on the lab floor. `CombatLab.playerAnchor.test.ts` settles a
 * real body under real physics and asserts it lands exactly here, which is the
 * check v1.0 lacked while the player was actually 41px above it.
 */
export const RULER_ANCHOR = { x: 700, y: LAB_FLOOR_TOP_Y - LAB_PLAYER_H } as const;
/** Bench station — off-axis bearings + the `maxTargets` cap. */
export const BENCH_ANCHOR = { x: 1600, y: LAB_FLOOR_TOP_Y - LAB_PLAYER_H } as const;

/**
 * Where combat MEASURES a player standing on that anchor (COMBAT-MELEE-CONE-04)
 * — the hitbox centre, +20/+20 from the top-left he stands at. Resolved through
 * the shipped helper, never re-derived, so if the anchor ever moves again the
 * ruler moves with it instead of quietly describing a point nobody measures at.
 *
 * ANCHOR = where the body is. ORIGIN = where the cone starts. Use the ORIGIN
 * for every distance, tick and bearing on this map.
 */
export const RULER_ORIGIN = playerCombatPoint({ ...RULER_ANCHOR, width: LAB_PLAYER_W, height: LAB_PLAYER_H });
export const BENCH_ORIGIN = playerCombatPoint({ ...BENCH_ANCHOR, width: LAB_PLAYER_W, height: LAB_PLAYER_H });

/** Ground targets stand on the main floor; their CENTRE is 5px ABOVE the origin
 *  (it was 15px BELOW the old corner anchor — the wedge used to sit on the head,
 *  which is half of what CONE-04 fixed). */
const GROUND_ROW_TOP_Y = LAB_FLOOR_TOP_Y - LAB_DUMMY_H;
const GROUND_DY = combatRowDy(GROUND_ROW_TOP_Y, RULER_ORIGIN.y);     // -5

/** Bench ledge: one-way so the player walks under it while dummies land on
 *  top. 60px up → their centres sit 65px ABOVE the origin. */
const BENCH_LEDGE_TOP_Y = LAB_FLOOR_TOP_Y - 60;
const BENCH_LEDGE_H = 10;                 // top 640 → bottom 650 = ground row top
const BENCH_ROW_TOP_Y = BENCH_LEDGE_TOP_Y - LAB_DUMMY_H;
const BENCH_DY = combatRowDy(BENCH_ROW_TOP_Y, BENCH_ORIGIN.y);       // -65

// ============================================================================
// RULER — ticks every 25px of TRUE distance, out past maxAreaRadius.
// ============================================================================

const RULER_STEP_PX = 25;
const RULER_MAX_PX = 350;          // > antiCheat.maxAreaRadius (300)
/**
 * Label every 100px, not every 50. At 50 the ruler printed 14 numbers across
 * 700px in a 12px monospace font and they ran into each other AND into the
 * dummy nameplates — one illegible band straight through the middle of the
 * stage, on a map whose other job is VFX observation. The ticks themselves are
 * still every 25px; only the TEXT is thinned.
 */
const RULER_LABEL_STEP_PX = 100;
const TICK_W = 3;
const TICK_H = 18;

/**
 * Distances that get a highlighted tick because a shipped number lands there.
 * Sourced from the per-class cone profiles + the AoE anti-cheat ceiling; kept
 * as data so a retune is a one-line edit here and in the profile TOML.
 */
export const RULER_PROFILE_MARKS: ReadonlyArray<{ distancePx: number; note: string; color: string }> = [
  { distancePx: 138, note: 'rogue melee',   color: COLOR_PROFILE },
  { distancePx: 188, note: 'shaman',  color: COLOR_PROFILE },
  { distancePx: 219, note: 'paladin', color: COLOR_PROFILE },
  { distancePx: 238, note: 'warrior', color: COLOR_PROFILE },
  { distancePx: 300, note: 'AoE cap', color: COLOR_CAP },
];

/** Half-arcs the protractor marks — `isInCone` compares bearing to arcDeg/2. */
export const PROTRACTOR_MARKS: ReadonlyArray<{ halfArcDeg: number; note: string }> = [
  { halfArcDeg: 27.5, note: 'rogue 55' },
  { halfArcDeg: 50,   note: 'shaman 100' },
  { halfArcDeg: 55,   note: 'paladin 110' },
  { halfArcDeg: 60,   note: 'warrior 120' },
];

// ============================================================================
// TARGETS — every dummy, with its exact combat-space distance + bearing.
// ============================================================================

export interface LabTarget {
  /** Entity id — stable across boots so probes can address one by name. The
   *  last three characters are the declared distance; probes parse them. */
  readonly id: string;
  readonly station: 'ruler' | 'bench';
  /** Which facing this target is reachable from. */
  readonly side: 'left' | 'right';
  /** TRUE radial distance from the station anchor, CLIENT cone space. */
  readonly distancePx: number;
  /** Bearing off the facing axis, degrees. 0 = dead ahead. */
  readonly bearingDeg: number;
  /** The same target measured the SERVER's way, through the helper the server's
   *  area scan actually calls. Since COMBAT-MELEE-CONE-03 that is the same
   *  anchor as `distancePx`, so the two are equal — carried (and asserted) so a
   *  future re-divergence shows up as a number rather than as lost HP. */
  readonly serverDistancePx: number;
  /** Authored world x (raw `npc.x`, hitbox left edge). */
  readonly x: number;
  /** Authored world y (raw `npc.y`, hitbox TOP). */
  readonly y: number;
  /** The point the client cone actually hit-tests (sprite centre). */
  readonly combatX: number;
  readonly combatY: number;
  /** Nameplate text — the label a screenshot carries with no tooling. Kept
   *  SHORT on purpose: 13px nameplates 45px apart collide the moment they
   *  carry a second field (2026-07-26 — the stage was unreadable). */
  readonly displayName: string;
}

/** Build a target from its intended COMBAT distance on a given row. */
function makeTarget(
  id: string,
  station: 'ruler' | 'bench',
  origin: { x: number; y: number },
  side: 'left' | 'right',
  distancePx: number,
  rowTopY: number,
  rowDy: number,
  displayName: string,
): LabTarget {
  const dx = offsetForDistance(distancePx, rowDy);
  if (dx === null) throw new Error(`[combat-lab] ${distancePx}px is inside the ${rowDy}px off-axis offset`);
  const signed = side === 'right' ? dx : -dx;
  // Solve BACKWARDS from the combat point to the authored corner, so the thing
  // that ends up exact is the number combat reads.
  const combatX = origin.x + signed;
  const combatY = origin.y + rowDy;
  const x = combatX - LAB_DUMMY_W / 2;
  const y = rowTopY;
  // Measured through the SERVER's own helper, not re-derived: if the server
  // ever moves off this anchor again, this number moves with it and
  // CombatLab.geometry.test.ts fails on the parity assertion.
  const serverPoint = mobCombatPoint({ x, y, width: LAB_DUMMY_W, height: LAB_DUMMY_H });
  return {
    id,
    station,
    side,
    distancePx,
    bearingDeg: bearingDeg(dx, rowDy),
    serverDistancePx: Math.hypot(serverPoint.x - origin.x, serverPoint.y - origin.y),
    x,
    y,
    combatX,
    combatY,
    displayName,
  };
}

const groundTarget = (
  station: 'ruler' | 'bench',
  origin: { x: number; y: number },
  side: 'left' | 'right',
  distancePx: number,
  idSuffix = String(distancePx).padStart(3, '0'),
  displayName = `${distancePx}px`,
): LabTarget =>
  makeTarget(`lab_${station}_${side}_${idSuffix}`, station, origin, side, distancePx, GROUND_ROW_TOP_Y, GROUND_DY, displayName);

const benchTarget = (side: 'left' | 'right', distancePx: number): LabTarget => {
  const t = makeTarget(
    `lab_bench_${side}_${String(distancePx).padStart(3, '0')}`,
    'bench', BENCH_ORIGIN, side, distancePx, BENCH_ROW_TOP_Y, BENCH_DY,
    `${distancePx}px`,
  );
  return { ...t, displayName: `${distancePx}∠${t.bearingDeg.toFixed(0)}°` };
};

/**
 * Historical ruler ladders retain their exact target IDs and distances. After
 * the October 4 reach extension, warrior and paladin share a 3/3 ruler count;
 * their bench target caps distinguish the complete two-station signature.
 *
 * The near right mark is 80, not 60: the sprite-centre correction moves raw x
 * 30px toward the player, and a 60px mark would put a dummy's body inside the
 * player's AABB (bump → the ruler moves → the whole point is lost).
 * Original bands (110/150/175/190) remain useful frozen regression controls;
 * current cone reach is 138/188/219/238. The ruler is for cones and the AoE
 * ceiling, not the ranged rogue basic (600px).
 */
const RULER_RIGHT_DISTANCES = [80, 130, 182, 240, 310] as const;
const RULER_LEFT_DISTANCES = [100, 160, 210, 270, 330] as const;

/**
 * Bench row (centres 65px ABOVE the origin) — these discriminate on ARC.
 *   right 90  → 46.2°: outside rogue's 27.5° half-arc, inside shaman's 50°.
 *   right 126 → 31.1°: same band; the second body that pushes shaman's
 *                      3-target cap past its limit.
 *   right 167 → 22.9°: inside every arc; rogue's 138px reach misses, while
 *                      shaman/paladin/warrior reach includes it.
 *   left  82  → 52.4°: inside every REACH, between shaman's 50° and paladin's
 *                      55° — the shaman/paladin arc split.
 *   left  185 → 20.6°: inside shaman/paladin/warrior reach, outside rogue's.
 *
 * ── RETUNED for CONE-04, and one capability moved ──────────────────────────
 * The old ladder (right 57/95/140, left 53/185) was solved for a 45px off-axis
 * offset. The origin moved 20px DOWN, the offset became 65px, and 57 and 53 are
 * now SHORTER than the offset itself — there is no x at which a body 65px above
 * you is 53px away, and `makeTarget` throws rather than author a fiction.
 *
 * Re-solving is not a free relabelling, because a bigger `dy` compresses the
 * bearing bands in x: at 65px off-axis the 50–55° band sits at dx≈50 and the
 * 27.5–50° band at dx 55–115, so two targets one in each are ~12px apart —
 * under the 42px mob-separation floor, i.e. they would shove each other and the
 * ruler would drift. They cannot both live on the same side any more.
 *
 * `maxTargets` clamping WINS that trade (it is the reason the bench exists —
 * flat ground physically cannot exceed a cap). The original right ladder
 * carried two shaman-eligible bodies and moved the shaman/paladin ARC split
 * to the LEFT. Extended reach includes all three right bodies; warrior versus
 * paladin now remains distinct through their five-versus-four target caps.
 */
const BENCH_RIGHT_DISTANCES = [90, 126, 167] as const;
const BENCH_LEFT_DISTANCES = [82, 185] as const;

/**
 * Flat cluster under the bench. With the bench row this puts 6 targets inside
 * each warrior, paladin and shaman wedge, so `maxTargets`
 * (5, 4 and 3) actually clamps for all three. 80 is the nearest that clears the
 * player AABB; ≥45px apart after that, the tightest the 42px separation floor
 * allows.
 */
const BENCH_CLUSTER_DISTANCES = [80, 125, 170] as const;

export const LAB_TARGETS: readonly LabTarget[] = [
  ...RULER_RIGHT_DISTANCES.map((d) => groundTarget('ruler', RULER_ORIGIN, 'right', d)),
  ...RULER_LEFT_DISTANCES.map((d) => groundTarget('ruler', RULER_ORIGIN, 'left', d)),
  ...BENCH_RIGHT_DISTANCES.map((d) => benchTarget('right', d)),
  ...BENCH_LEFT_DISTANCES.map((d) => benchTarget('left', d)),
  ...BENCH_CLUSTER_DISTANCES.map((d) =>
    groundTarget('bench', BENCH_ORIGIN, 'right', d, `c${String(d).padStart(3, '0')}`, `${d}px ▪`)),
];

export {
  GROUND_DY, BENCH_DY, GROUND_ROW_TOP_Y, BENCH_ROW_TOP_Y, BENCH_LEDGE_TOP_Y,
  BENCH_LEDGE_H, RULER_STEP_PX, RULER_MAX_PX, RULER_LABEL_STEP_PX, TICK_W, TICK_H,
};
